import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import PageHeading from './PageHeading';

const TIKTOK_VIDEO_ID = '7692480775496666381';
const TIKTOK_URL = `https://www.tiktok.com/@naturesnefer/video/${TIKTOK_VIDEO_ID}`;

const CocoaIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M7 10h8.2v5.2A3.4 3.4 0 0 1 11.8 18.6H10.2A3.2 3.2 0 0 1 7 15.4V10Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M15.2 11.4h1.7a2.1 2.1 0 0 1 0 4.2h-1.7"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M9.2 7.6c.5-1.1.2-1.8-.1-2.6M12.2 7.6c.5-1.1.2-1.8-.1-2.6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3.2 13.3 8.2 18.4 9.5 13.3 10.8 12 15.8 10.7 10.8 5.6 9.5 10.7 8.2 12 3.2Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M17.8 14.2 18.4 16.2 20.4 16.8 18.4 17.4 17.8 19.4 17.2 17.4 15.2 16.8 17.2 16.2 17.8 14.2Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </svg>
);

const STEPS = [
  'Place the bottle in hot, not boiling, water until the oil becomes liquid. Never microwave.',
  'Heat test first before applying to your hair or scalp.',
  'Skin test a small area before full use.',
  'Apply to damp hair, section by section, and massage into the scalp. Comb through from roots to ends.',
  'Use it before or after your shower, depending on your routine.',
  'Use it as a treatment mask and wash it out, or leave it in depending on your hair type and what works best for you.',
];

function readPlayerMessage(data) {
  if (!data || typeof data !== 'object') return null;
  if (data['x-tiktok-player'] || data.type === 'onError') return data;
  return null;
}

function browserBlocksTikTok() {
  return /Ddg\/|DuckDuckGo/i.test(window.navigator.userAgent || '');
}

function TikTokVideo() {
  const [blocked, setBlocked] = useState(browserBlocksTikTok);

  useEffect(() => {
    if (blocked) return undefined;
    const onMessage = (event) => {
      if (event.origin !== 'https://www.tiktok.com') return;
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (err) {
          return;
        }
      }
      const message = readPlayerMessage(data);
      if (message && message.type === 'onError') setBlocked(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [blocked]);

  if (blocked) {
    return (
      <div className="faq-video">
        <a
          className="faq-video-fallback"
          href={TIKTOK_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="faq-video-play" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 7.5v9l8-4.5-8-4.5Z" />
            </svg>
          </span>
          <span className="faq-video-fallback-title">How to Use H.O.T. Cocoa</span>
          <span className="faq-video-fallback-copy">
            Your browser blocked the in-page player. Watch it on TikTok.
          </span>
        </a>
      </div>
    );
  }

  return (
    <div className="faq-video">
      <iframe
        src={`https://www.tiktok.com/player/v1/${TIKTOK_VIDEO_ID}?music_info=1&description=0&rel=0`}
        title="How to Use H.O.T. Cocoa on TikTok"
        allow="fullscreen"
        allowFullScreen
      />
      <a
        className="faq-video-link"
        href={TIKTOK_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Watch on TikTok
      </a>
    </div>
  );
}

function FaqPage({ embedded }) {
  return (
    <Container className={`Center-column faq-page${embedded ? ' faq-home' : ''}`}>
      {embedded ? null : <div className="pg-break"> </div>}
      <PageHeading
        as={embedded ? 'h2' : 'h1'}
        icon="leaf"
        className="faq-title"
      >
        How to Use H.O.T. Cocoa
      </PageHeading>
      <p className="faq-kicker">Nature&apos;s Nefer</p>

      <div className="faq-content">
        <TikTokVideo />

        <div className="faq-copy">
        <p>
          Meet Nature&apos;s Nefer, a self-care brand inspired by nature,
          community, and everyday needs. Founder Youssef A. created the first
          H.O.T. (Hot Oil Treatment) recipe in his own kitchen to address his
          hair, beard, and skin needs. What started as a personal solution grew
          into a passion for creating simple, thoughtfully made products.
        </p>

        <h2 className="faq-heading">
          <CocoaIcon />
          What is H.O.T. Cocoa?
        </h2>
        <p>
          H.O.T. Cocoa is a nourishing hot oil treatment made with cocoa butter,
          shea butter, mango butter, argan oil, sesame oil, bhringaraj, castor
          oil, moringa oil, and lavender oil. It can be used as a hair and
          scalp treatment and may also be used on facial hair.
        </p>

        <h2 className="faq-heading">How to use it</h2>
        <ol className="faq-steps">
          {STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>

        <p className="faq-close">
          <SparkleIcon />
          Your hair, your routine, your way.
        </p>
        <p className="faq-signoff">
          Nature&apos;s Nefer — bringing together nature, community, and
          everyday self-care.
        </p>
        </div>
      </div>
    </Container>
  );
}

export default FaqPage;
